/**
 * Typed client for the platform-admin console (/api/admin/tenants/*).
 * Gated server-side by the `platform:admin` permission.
 */
import { authFetch } from "@/auth/fetchClient";

export interface PlatformTenant {
  id: string;
  slug: string;
  name: string;
  status: string;
  zitadel_org_id: string | null;
  timezone?: string;
  created_at?: string;
}

export interface CreateTenantResult extends PlatformTenant {
  initial_admin: { email: string; temp_password: string; user_id: string };
}

export interface TenantUser {
  user_id: string;
  username: string | null;
  email: string | null;
  given_name: string | null;
  family_name: string | null;
  state: string | null;
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: string; error_description?: string }
      | null;
    throw Object.assign(
      new Error(body?.error_description ?? body?.error ?? `http_${res.status}`),
      { code: body?.error, status: res.status },
    );
  }
  return (await res.json()) as T;
}

const BASE = "/api/admin/tenants/";

export async function listTenants(): Promise<PlatformTenant[]> {
  const res = await authFetch(BASE);
  return (await jsonOrThrow<{ items: PlatformTenant[] }>(res)).items;
}

export async function createTenant(body: {
  slug: string;
  name: string;
  admin_email: string;
  admin_given_name?: string;
  admin_family_name?: string;
  timezone?: string;
}): Promise<CreateTenantResult> {
  const res = await authFetch(BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow<CreateTenantResult>(res);
}

export async function updateTenant(tenantId: string, body: { name: string }): Promise<PlatformTenant> {
  const res = await authFetch(`${BASE}${tenantId}/`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow<PlatformTenant>(res);
}

export async function setTenantStatus(tenantId: string, active: boolean): Promise<PlatformTenant> {
  const res = await authFetch(`${BASE}${tenantId}/status/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ active }),
  });
  return jsonOrThrow<PlatformTenant>(res);
}

export async function listTenantUsers(tenantId: string): Promise<TenantUser[]> {
  const res = await authFetch(`${BASE}${tenantId}/users/`);
  return (await jsonOrThrow<{ items: TenantUser[] }>(res)).items;
}

export async function createTenantUser(
  tenantId: string,
  body: { email: string; given_name?: string; family_name?: string; role?: string },
): Promise<{ user_id: string; email: string; temp_password: string }> {
  const res = await authFetch(`${BASE}${tenantId}/users/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

export async function deleteTenantUser(tenantId: string, userId: string): Promise<void> {
  const res = await authFetch(`${BASE}${tenantId}/users/${userId}/`, { method: "DELETE" });
  if (!res.ok && res.status !== 204) await jsonOrThrow(res);
}

export async function resetTenantUserPassword(
  tenantId: string,
  userId: string,
): Promise<{ temp_password: string }> {
  const res = await authFetch(`${BASE}${tenantId}/users/${userId}/reset-password/`, {
    method: "POST",
  });
  return jsonOrThrow(res);
}

// ---- platform admins (users of the dedicated 'platform' tenant) ----------

const ADMINS = "/api/admin/platform-admins/";

export async function listPlatformAdmins(): Promise<TenantUser[]> {
  const res = await authFetch(ADMINS);
  return (await jsonOrThrow<{ items: TenantUser[] }>(res)).items;
}

export async function addPlatformAdmin(body: {
  email: string;
  given_name?: string;
  family_name?: string;
}): Promise<{ user_id: string; email: string; temp_password: string }> {
  const res = await authFetch(ADMINS, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(res);
}

export async function removePlatformAdmin(userId: string): Promise<void> {
  const res = await authFetch(`${ADMINS}${userId}/`, { method: "DELETE" });
  if (!res.ok && res.status !== 204) await jsonOrThrow(res);
}

export interface TenantPricing {
  internet?: string; // IOD, USD/Mbps/month
  ethernet?: string; // EOD/EVC
}

export async function getTenantPricing(tenantId: string): Promise<TenantPricing> {
  const res = await authFetch(`${BASE}${tenantId}/pricing/`);
  return jsonOrThrow<TenantPricing>(res);
}

export async function setTenantPricing(
  tenantId: string,
  rates: { internet: string | null; ethernet: string | null },
): Promise<TenantPricing> {
  const res = await authFetch(`${BASE}${tenantId}/pricing/`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(rates),
  });
  return jsonOrThrow<TenantPricing>(res);
}
